import { createTheme } from '@mui/material/styles'

export const appColors = {
  modalBackColor: '#192734',
  componentBackColor: '#133f60',
  windowBackColor: '#2d2d2d',
  itemBackColor: '#202020',
  headerBackgroundColor: '#D8F2FF',
  borderColor: '#000000',
  borderColorLight: '#c0c0c0CC',
  borderColorDark: '#c0c0c080',
  headerTextColor: '#C0C0C0',
  itemTextColor: '#429CEB',
  itemTextColorMobile: '#34C9EB',
  accentColor: '#4BB2F9',
  rotateColor: '#8f3044A3',
  slideColor: '#156092A3',
  nptColor: '#FF6363EF',
  bhaHandlingColor: '#FFEF00EF',
  dirNptColor: '#FFB35CEF',
  motorColor: '#F59B00',
  mwdColor: '#59FF00CF',
  bitColor: '#FF6363EF',
  surveyTrajectoryColor: '#FF5733',
  planTrajectoryColor: '#0096FF',
  principalPlanColor: '#6495ED',
  mapClusterColor: '#0782f5',
  mapClusterTextColor: '#FFFFFF',
  homePageCardColor: '#FFF',
  itemTextColorDark: '#136cb9',
  itemTextColorDark2: '#115ea2',
  itemTextColorDark3: '#0f518a',
  chartNeonBlue: '#34C9EB',
  avgColor: '#91A6FFA3',
  pdfTableColor: 'rgb(135, 206, 250, .25)',
  altRowColor: '#EFEFEF',
  pdfBorderColor: 'rgb(135, 206, 250)',
  success: '#00dc78',
  failure: '#FF0000',
  cleanStatusColor: '#4CBB17',
  dirtyStatusColor: '#FF0000',
  defaultTextColor: '#000000',
  aggridColors: {
    dark: {
      backgroundDarker: '#181d1f',
      backgroundLighter: '#222628',
      hover: 'rgba(33, 150, 243, 0.1)',
    },
    light: {
      backgroundDarker: '#FCFCFC',
      backgroundLighter: '#FFFFFF',
    },
  },
}

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#429CEB',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#3C3C3C',
      contrastText: '#000',
    },
    action: {
      active: appColors.headerTextColor,
    },
    itemBackground: '#ffffff',
    background: {
      default: '#f9fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
    },
  },
  components: {
    // Added for symmetry with dark theme; should not affect sizing.
    MuiCssBaseline: {
      styleOverrides: {
        html: { colorScheme: 'light' },
        body: { colorScheme: 'light' },
      },
    },

    MuiInput: {
      defaultProps: { disableUnderline: true },
    },

    MuiDialogTitle: {
      styleOverrides: { root: { color: '#000' } },
    },

    MuiFormControlLabel: {
      styleOverrides: { root: { color: '#000' } },
    },

    // Keep this very minimal: only background/color, no sizing.
    MuiTextField: {
      defaultProps: {
        size: 'small', // instead of height: '32px'
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          backgroundColor: '#fff', // or appColors.itemBackColor in dark
          color: appColors.itemTextColor,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          color: appColors.itemTextColor,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',

          // ✅ This is now the border you see
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d7de', // light grey border similar to your earlier setup
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.accentColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.accentColor,
            boxShadow: '0 0 6px rgba(66,156,235,0.3)',
          },

          '& input::placeholder': {
            color: '#808b96',
            opacity: 1,
          },
          '&.Mui-focused input::placeholder': {
            opacity: 0.5,
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          // ❌ Removed paddingLeft so we don't fight OutlinedInput's layout
          color: appColors.itemTextColor,
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px #fff inset !important`,
            WebkitTextFillColor: `${appColors.itemTextColor}`,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#5f6b76',
          transition: 'color 0.2s ease',
          '&.Mui-focused': {
            color: '#429CEB',
          },

          // ❗ Removed all transform overrides so MUI can handle label positioning.
          // Those transforms often break Select / filled variants.
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          color: '#000',
          backgroundColor: '#fff',
          border: '1px solid #d0d7de',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        },
        option: {
          color: '#1a1a1a',
          '&:hover': {
            backgroundColor: 'rgba(66,156,235,0.08)',
          },
        },

        // ❗ Removed inputRoot override entirely.
        // It was forcing minHeight, display:flex, padding, nested overrides, etc.,
        // all of which can easily clamp or fight TextField/Select sizing.
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: () => ({
          overflow: 'hidden',
          backgroundColor: '#385d85',
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: () => ({
          px: 1,
          gap: 4,
          height: 32,
          lineHeight: 1,
          textTransform: 'none',
          borderColor: appColors.itemBackColor,
          color: '#999',

          '&:hover': {
            backgroundColor: '#c0c0c019',
          },

          '&.Mui-selected': {
            backgroundColor: '#c0c0c026',

            color: '#fff',

            '&:hover': {
              backgroundColor: '#c0c0c026',
            },
          },
        }),
      },
    },
  },
})

export const darkTheme = createTheme({
  palette: {
    primary: {
      main: '#429CEB',
      contrastText: '#FFF',
    },
    secondary: {
      main: appColors.headerTextColor,
      contrastText: '#fff',
    },
    action: {
      active: appColors.headerTextColor,
    },
    itemBackground: appColors.modalBackColor,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { colorScheme: 'dark' },
        body: { colorScheme: 'dark' },
      },
    },

    MuiInput: {
      defaultProps: { disableUnderline: true },
    },

    MuiDialogTitle: {
      styleOverrides: { root: { color: appColors.headerTextColor } },
    },

    MuiFormControlLabel: {
      styleOverrides: { root: { color: appColors.mapClusterTextColor } },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: appColors.headerTextColor,
        },
      },
    },

    MuiTreeView: {
      styleOverrides: {
        root: {
          '&::-webkit-scrollbar': {
            width: '16px',
            backgroundColor: '#192734',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3) !important',
            WebkitBoxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3) !important',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: appColors.componentBackColor,
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          color: appColors.itemTextColor,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.aggridColors.dark.backgroundDarker,
          color: appColors.itemTextColor,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2f3a45',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.accentColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.accentColor,
            boxShadow: '0 0 6px rgba(61,184,255,0.35)',
          },

          '& input::placeholder': {
            color: '#aab4be',
            opacity: 1,
          },
          '&.Mui-focused input::placeholder': {
            opacity: 0.6,
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          color: appColors.itemTextColor,
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${appColors.windowBackColor} inset !important`,
            WebkitTextFillColor: `${appColors.itemTextColor}`,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#aab4be',
          transition: 'color 0.2s ease',

          '&.Mui-focused': {
            color: '#3db8ff',
          },
        },
      },
    },

    MuiPickersPopper: {
      styleOverrides: {
        root: {
          zIndex: 1700,
          color: appColors.headerTextColor,
          '& .MuiPaper-root': {
            backgroundColor: appColors.windowBackColor,
            color: appColors.mapClusterTextColor,
            border: `1px solid ${appColors.borderColor}`,
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          },
        },
      },
    },

    MuiDayPicker: {
      styleOverrides: {
        weekDayLabel: { color: appColors.mapClusterTextColor },
      },
    },

    MuiPickersDay: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.windowBackColor,
          color: appColors.mapClusterTextColor,
          '&:hover': {
            color: '#fff',
            border: `1px solid ${appColors.mapClusterTextColor}`,
          },
          '&.Mui-selected': {
            color: '#fff',
            border: `1px solid ${appColors.mapClusterTextColor}`,
            '&:hover': {
              backgroundColor: appColors.accentColor,
              color: '#fff',
            },
          },
          '&.MuiPickersDay-today': {
            borderColor: appColors.accentColor,
            color: appColors.accentColor,
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: appColors.accentColor,
              color: '#fff',
            },
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          color: appColors.headerTextColor,
          backgroundColor: appColors.aggridColors.dark.backgroundLighter,
          scrollbarWidth: 'thin',
          colorScheme: 'dark',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: appColors.headerTextColor,
          borderRadius: 8,
          marginRight: 8,
          marginLeft: 8,
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: appColors.aggridColors.dark.hover,
          },
          '&.Mui-selected': {
            backgroundColor: appColors.aggridColors.dark.backgroundSelected,
            color: appColors.headerTextColor,
            '&:hover': {
              backgroundColor: appColors.aggridColors.dark.backgroundSelectedHover,
            },
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          color: '#FFFFFF',
          backgroundColor: appColors.aggridColors.dark.backgroundDarker,
          border: `1px solid ${appColors.headerTextColor}`,
        },

        option: {
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
          },
          '&:nth-of-type(odd)': {
            backgroundColor: appColors.aggridColors.dark.backgroundDarker,
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
            },
          },
          '&:nth-of-type(even)': {
            backgroundColor: appColors.aggridColors.dark.backgroundLighter,
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
            },
          },
        },

        // ❗ Removed inputRoot override here as well for the same reason as light:
        // it was duplicating OutlinedInput styling and can easily clamp the height.
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: appColors.headerTextColor,
          '&.Mui-active': {
            color: '#1976d2',
          },
          '&.Mui-completed': {
            color: '#4caf50',
          },
          '&.Mui-error': {
            color: '#d32f2f',
          },
        },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: {
        root: () => ({
          overflow: 'hidden',
          backgroundColor: '#181d1f',
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: () => ({
          px: 1,
          gap: 4,
          height: 32,
          lineHeight: 1,
          textTransform: 'none',
          color: '#fff',
          borderColor: appColors.borderColorLight,

          '&:hover': {
            backgroundColor: '#c0c0c01a',
          },

          '&.Mui-selected': {
            backgroundColor: '#c0c0c010',

            color: appColors.itemTextColor,

            '&:hover': {
              backgroundColor: '#c0c0c010',
            },
          },
        }),
      },
    },
  },
})

export const chartSeriesColors = [
  '#5A87C6',
  '#DB3C94',
  '#3B21D9',
  '#B0ED27',
  '#A30A73',
  '#3EE371',
  '#2852D7',
  '#0BF629',
  '#E241DC',
  '#c7b295',
  '#515354',
  '#19C9F9',
  '#124408',
  '#0EB9AF',
  '#0B9649',
  '#518726',
  '#E3C340',
  '#9D9563',
  '#4FDC62',
  '#543851',
  '#1D5C19',
  '#707EDB',
  '#22AEDF',
  '#4DF59E',
  '#70BB7C',
  '#4C60B3',
  '#6C41B1',
  '#FB33EB',
  '#9DB592',
  '#1CE760',
  '#9B6CB2',
  '#DBCFDB',
  '#B8D635',
  '#8A23FA',
  '#51747C',
  '#CE83B3',
]
